import { Button, IconButton, Progress, cn } from '@glade-ai/ui';
import { nanoid } from 'nanoid';
import promiseRetry from 'promise-retry';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Upload } from 'tus-js-client';

import { NOODLE_MEDIA_UPLOAD_HOST } from '@/configuration/client';
import getS3OriginIdFromUrl from '@/helpers/tusd/getS3OriginIdFromUrl';
import useUser from '@/providers/Auth/useUser';
import { logError } from '@/providers/ErrorTracking';
import { mixpanelTrack } from '@/providers/Mixpanel';
import { useScreenRecord } from '@/providers/ScreenRecord';
import { getMediaData } from '@/tsClient';
import * as ApiModels from '@/typings/api-models';

/** Format seconds as mm:ss. */
const formatTime = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

/** Format bytes as a human-readable file size string. */
const formatFileSize = (bytes: number): string => {
  const megabytes = bytes / (1024 * 1024);
  return `${megabytes.toFixed(1)} MB`;
};

type UploadState = 'idle' | 'uploading' | 'processing' | 'success' | 'error';

/** Global preview dialog shown after recording stops. Allows upload or discard. Uses createPortal to guarantee visibility above all page elements. */
const RecordingPreviewDialog: FC = () => {
  const {
    recordedBlob,
    recordedDurationSeconds,
    isRecording,
    recordedWithMicAudio,
    clearRecording,
    addUploadedRecording,
    maxDurationSeconds,
  } = useScreenRecord();
  const [user] = useUser();
  const tusUploadRef = useRef<Upload | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isActiveRef = useRef(true);

  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [videoSrc, setVideoSrc] = useState<string | undefined>();

  const isOpen = !!recordedBlob && !isRecording;
  const isMaxDuration = recordedDurationSeconds >= maxDurationSeconds;

  // Manage object URL lifecycle for video preview
  useEffect(() => {
    if (!recordedBlob) {
      setVideoSrc(undefined);
      return;
    }
    const url = URL.createObjectURL(recordedBlob);
    setVideoSrc(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [recordedBlob]);

  // Reset upload state when dialog opens with a new recording
  useEffect(() => {
    if (isOpen) {
      isActiveRef.current = true;
      setUploadState('idle');
      setUploadPercentage(0);
      setUploadError(null);
    }
    return () => {
      isActiveRef.current = false;
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, [isOpen]);

  // Focus the dialog content when it opens
  useEffect(() => {
    if (isOpen && contentRef.current) {
      contentRef.current.focus();
    }
  }, [isOpen]);

  const handleDiscard = useCallback(() => {
    // Abort any in-progress upload
    if (tusUploadRef.current) {
      tusUploadRef.current.abort(true).catch(() => {});
      tusUploadRef.current = null;
    }
    mixpanelTrack('screen_recording_discarded', { durationSeconds: recordedDurationSeconds });
    clearRecording();
  }, [clearRecording, recordedDurationSeconds]);

  const handleUpload = useCallback(() => {
    if (!recordedBlob) return;

    const filename = `screen-recording-${new Date().toISOString()}.webm`;
    const file = new File([recordedBlob], filename, { type: 'video/webm' });
    const fileSizeMb = file.size / (1024 * 1024);

    setUploadState('uploading');
    setUploadPercentage(0);
    setUploadError(null);

    const headers: ConstructorParameters<typeof Upload>[1]['headers'] = {};
    if (user?.token) {
      headers.authorization = user.token;
    }

    const tusUpload = new Upload(file, {
      endpoint: `${NOODLE_MEDIA_UPLOAD_HOST}/uploads`,
      headers,
      metadata: {
        contentType: 'video/webm',
        filename,
        filetype: 'video/webm',
        referenceId: nanoid(),
        referenceType: 'screen-recording',
      },
      onError: (error) => {
        if (!isActiveRef.current) return;
        logError(error, { tags: { flow: 'screen-recording-upload' } });
        setUploadState('error');
        setUploadError(error.message);
        tusUploadRef.current = null;
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        const percentage = Math.round((bytesUploaded / bytesTotal) * 100);
        setUploadPercentage(percentage);
      },
      onSuccess: async () => {
        if (!isActiveRef.current) return;
        setUploadState('processing');
        const s3OriginId = getS3OriginIdFromUrl(tusUpload.url);

        try {
          if (!s3OriginId) {
            throw new Error(`Could not extract s3OriginId from ${tusUpload.url}`);
          }

          const mediaData = await promiseRetry(
            async (retry) => {
              const response = await getMediaData({ s3OriginId });
              // Fail fast on permanent errors — do not retry
              if (response.mediaStatus === ApiModels.MediaStatus.Error) {
                throw new Error('Upload processing failed');
              }
              // Retry only while still processing
              if (response.mediaStatus !== ApiModels.MediaStatus.Completed) {
                return retry(new Error('Still processing'));
              }
              return response;
            },
            { factor: 1, minTimeout: 1000, retries: 30 },
          );

          if (!isActiveRef.current) return;

          if (!mediaData.id) {
            throw new Error('No media ID returned after upload');
          }

          mixpanelTrack('screen_recording_uploaded', {
            durationSeconds: recordedDurationSeconds,
            fileSizeMb,
            mediaId: mediaData.id,
          });

          addUploadedRecording({
            mediaId: mediaData.id,
            name: filename,
            durationSeconds: recordedDurationSeconds,
            fileSizeMb,
            uploadedAt: new Date(),
          });

          setUploadState('success');
          tusUploadRef.current = null;

          // Auto-close after a brief delay to show success state
          autoCloseTimerRef.current = setTimeout(() => {
            if (!isActiveRef.current) return;
            clearRecording();
          }, 1200);
        } catch (processingError) {
          if (!isActiveRef.current) return;
          logError(processingError, { tags: { flow: 'screen-recording-upload' } });
          setUploadState('error');
          setUploadError(processingError instanceof Error ? processingError.message : 'Upload processing failed');
          tusUploadRef.current = null;
        }
      },
      removeFingerprintOnSuccess: true,
      retryDelays: [0, 3000, 5000, 10000, 20000],
    });

    tusUploadRef.current = tusUpload;
    tusUpload.start();
  }, [recordedBlob, user, recordedDurationSeconds, addUploadedRecording, clearRecording]);

  const isUploading = uploadState === 'uploading' || uploadState === 'processing';

  // Handle Escape key to discard (when not uploading)
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape' && !isUploading) {
        handleDiscard();
      }
    },
    [isUploading, handleDiscard],
  );

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div role='none' className='fixed inset-0 z-[9999] flex items-center justify-center'>
      {/* Backdrop */}
      <div role='none' className='absolute inset-0 bg-black/50' onClick={isUploading ? undefined : handleDiscard} />

      {/* Dialog content */}
      <div
        ref={contentRef}
        role='dialog'
        aria-modal='true'
        aria-label='Review Screen Recording'
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className={cn(
          'relative w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg',
          'flex flex-col gap-4',
          'max-h-[90dvh] overflow-y-auto',
          'focus:outline-none',
        )}
      >
        {/* Close button */}
        {!isUploading && (
          <IconButton
            icon='ph--x--regular'
            label='Close'
            iconOnly
            size='sm'
            variant='ghost'
            className='absolute top-4 end-4'
            onClick={handleDiscard}
          />
        )}

        {/* Header */}
        <header role='none'>
          <h2 className='text-lg font-semibold leading-none tracking-tight'>Review Screen Recording</h2>
          <p className='mt-1.5 text-sm text-on-surface-variant'>
            {isMaxDuration
              ? 'Maximum recording time (15 minutes) reached.'
              : 'Preview your recording before uploading.'}
          </p>
        </header>

        <figure role='group' className='flex flex-col gap-3'>
          {/* Video preview */}
          {videoSrc && (
            <video
              src={videoSrc}
              controls
              preload='metadata'
              className='w-full rounded-lg'
              aria-label='Screen recording preview'
            />
          )}

          {/* Metadata */}
          <figcaption className='flex items-center gap-2 text-sm text-on-surface-variant'>
            <span>{formatTime(recordedDurationSeconds)}</span>
            <span role='none' className='h-3 w-px bg-outline/30' />
            <span>{recordedBlob ? formatFileSize(recordedBlob.size) : ''}</span>
            <span role='none' className='h-3 w-px bg-outline/30' />
            <span>{recordedWithMicAudio ? 'Includes voice narration' : 'No audio'}</span>
          </figcaption>
        </figure>

        {/* Upload progress */}
        {isUploading && (
          <div role='status' className='flex flex-col gap-2'>
            <Progress value={uploadState === 'processing' ? 100 : uploadPercentage} />
            <p className='text-sm text-on-surface-variant'>
              {uploadState === 'processing' ? 'Processing...' : `Uploading... ${uploadPercentage}%`}
            </p>
          </div>
        )}

        {/* Success message */}
        {uploadState === 'success' && (
          <p role='status' className='text-sm text-success'>
            Upload complete! You can attach this recording in any chat.
          </p>
        )}

        {/* Error message */}
        {uploadState === 'error' && (
          <p role='alert' className='text-sm text-danger'>
            Upload failed: {uploadError ?? 'Unknown error'}
          </p>
        )}

        {/* Actions */}
        <footer role='none' className='flex justify-end gap-2 pt-2'>
          <Button variant='ghost' onClick={handleDiscard} disabled={isUploading}>
            Discard
          </Button>
          {uploadState === 'error' ? (
            <Button variant='default' onClick={handleUpload}>
              Retry Upload
            </Button>
          ) : (
            <Button variant='default' onClick={handleUpload} disabled={isUploading || uploadState === 'success'}>
              {isUploading ? 'Uploading...' : 'Upload'}
            </Button>
          )}
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export default RecordingPreviewDialog;
